import { animate } from '@motionone/dom';

let observer: IntersectionObserver | null = null;

export function setupProjectAnimations() {
  // Clean up existing observer
  if (observer) {
    observer.disconnect();
    observer = null;
  }

  // Add the animation styles to the document
  const style = document.createElement('style');
  style.textContent = `
    .animate-on-view {
      opacity: 0;
      transform: translate3d(0, 40px, 0);
      filter: blur(4px);
      transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                 transform 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                 filter 0.8s cubic-bezier(0.16, 1, 0.3, 1);
      will-change: transform, opacity, filter;
    }
    
    .animate-on-view.visible {
      opacity: 1;
      transform: translate3d(0, 0, 0);
      filter: blur(0px);
    }
  `;
  document.head.appendChild(style);

  // Create new observer
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const element = entry.target as HTMLElement;
      
      if (entry.isIntersecting) {
        // Add a small delay based on data-delay attribute
        const delay = Number(element.getAttribute('data-delay') || 0) * 150;
        setTimeout(() => {
          element.classList.add('visible');
        }, delay);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '150px 0px'
  });

  // Observe all elements with animate-on-view class
  document.querySelectorAll('.animate-on-view').forEach(element => {
    observer?.observe(element);
  });

  // Return cleanup function
  return () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    style.remove();
  };
}

// Improved number animation with better performance
export function animateValue(start: number, end: number, duration: number, setter: (val: number) => void) {
  const startTime = performance.now();
  const change = end - start;
  
  // Use requestAnimationFrame for smoother animation
  function update(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Improved easing function
    const easeOutQuart = 1 - Math.pow(1 - progress, 4);
    const current = Math.round(start + (change * easeOutQuart));
    
    setter(current);
    
    if (progress < 1) {
      requestAnimationFrame(update);
    }
  }
  
  requestAnimationFrame(update);
} 